import clsx from 'clsx';
import type { ButtonHTMLAttributes, CSSProperties, FunctionComponent, HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

import { Button, type ButtonProps } from '../button/Button';
import { SkeletonBase } from '../skeleton/Skeleton';

export type TableColumnWidth = string;

export const calculateRowBorderRadius = (level: number, expanded: boolean, roundedBottom: boolean): number | string => {
    if (level !== 0 && !roundedBottom) return 0;
    if (level === 0 && !expanded) {
        return 'var(--radius-lg)';
    }

    if (level === 0 && expanded) {
        return 'var(--radius-lg) var(--radius-lg) 0 0';
    }

    return roundedBottom ? '0 0 var(--radius-lg) var(--radius-lg)' : 'var(--radius-lg)';
};

const calculateLevelOffset = (level: number): string => {
    switch (level) {
        case 0:
            return '0.4rem';
        case 1:
            return '3.4rem';
        case 2:
            return '6.4rem';
        case 3:
            return '9.4rem';
    }

    return '0';
};

export const TableContainer: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={clsx('flex flex-col h-full w-full bg-gray-100 rounded-xxl p-s font-sans', className)} {...props} />
);

export const TableGrid: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={clsx('grid grid-cols-[1fr] rounded-xxl overflow-hidden w-full', className)} {...props} />
);

export const TableHeader: FunctionComponent<
    HTMLAttributes<HTMLDivElement> & { readonly $columnWidths?: ReadonlyArray<TableColumnWidth> }
> = ({ $columnWidths, className, style, ...props }) => (
    <div
        className={clsx('grid rounded-xxl text-gray-800 text-sm font-medium leading-[1.25rem] gap-s', className)}
        style={{ gridTemplateColumns: $columnWidths?.join(' '), ...style }}
        {...props}
    />
);

export const TableHeaderCell: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={clsx('flex items-center text-left py-m px-l', className)} {...props} />
);

export const TableBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={clsx(
            'grid grid-cols-[1fr] h-full overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            className,
        )}
        {...props}
    />
));

export const TableRow: FunctionComponent<
    HTMLAttributes<HTMLElement> & { readonly $columnWidths?: ReadonlyArray<TableColumnWidth> }
> = ({ $columnWidths, className, style, ...props }) => (
    <article
        className={clsx(
            'grid bg-white-200 rounded-lg text-gray-700 gap-s mt-s relative transition-all duration-200 ease-in-out',
            className,
        )}
        style={{ gridTemplateColumns: $columnWidths?.join(' '), ...style }}
        {...props}
    />
);

type ExpandableRowProps = HTMLAttributes<HTMLElement> & {
    readonly $columnWidths?: ReadonlyArray<TableColumnWidth>;
    readonly $expanded?: boolean;
    readonly $isActionPending?: boolean;
    readonly $isCut?: boolean;
    readonly $isSelected?: boolean;
    readonly $level?: number;
    readonly $roundedBottom?: boolean;
};

export const TableExpandableRow: FunctionComponent<ExpandableRowProps> = ({
    $columnWidths,
    $expanded = false,
    $isActionPending = false,
    $isCut = false,
    $isSelected = false,
    $level = 0,
    $roundedBottom = false,
    className,
    style,
    ...props
}) => (
    <article
        className={clsx(
            'grid text-gray-700 cursor-pointer gap-s relative transition-[background-color] duration-[50ms] ease-in-out',
            '[&_[data-show-on-row-hovered]]:invisible hover:[&_[data-show-on-row-hovered]]:visible',
            'hover:bg-gray-200',
            $isCut && 'border border-dotted border-orange-600 opacity-80',
            $isActionPending && 'pending-copy-border pointer-events-none opacity-70',
            $isSelected ? 'bg-gray-200' : 'bg-white-200',
            className,
        )}
        style={{
            borderRadius: calculateRowBorderRadius($level, $expanded, $roundedBottom),
            gridTemplateColumns: $columnWidths?.join(' '),
            outline: $isSelected ? 'var(--color-orange-800)' : 'none',
            ...style,
        }}
        {...props}
    />
);

type TableTreeLineProps = HTMLAttributes<HTMLDivElement> & {
    readonly $currentIndex: number;
    readonly $horizontalLine: boolean;
    readonly $isFirstInGroup: boolean;
    readonly $isLastInGroup: boolean;
    readonly $level: number;
};

const verticalDottedGradient =
    'repeating-linear-gradient(transparent 0px, transparent 2px, var(--color-gray-700) 2px, var(--color-gray-700) 6px)';
const horizontalDottedGradient =
    'repeating-linear-gradient(to right, transparent 0px, transparent 2px, var(--color-gray-700) 2px, var(--color-gray-700) 6px)';

export const TableTreeLine: FunctionComponent<TableTreeLineProps> = ({
    $horizontalLine,
    $isFirstInGroup,
    $isLastInGroup,
    $level,
    className,
    style,
    ...props
}) => (
    <div
        className={clsx('absolute', className)}
        style={{
            background: verticalDottedGradient,
            bottom: $isLastInGroup ? 'calc(50%)' : 0,
            left: `calc(${calculateLevelOffset($level)} + 14px)`,
            top: $isFirstInGroup ? 'calc(20% + 5px - 35px)' : 0,
            width: 1,
            ...style,
        }}
        {...props}
    >
        <span
            style={{
                background: horizontalDottedGradient,
                content: '""',
                height: (!$isFirstInGroup || !$isLastInGroup) && $horizontalLine ? '1px' : '0px',
                left: 0,
                position: 'absolute',
                top: $isLastInGroup ? '100%' : 'calc(50%)',
                transform: 'translateY(-50%)',
                width: !$isFirstInGroup ? '10px' : '0px',
                zIndex: 1,
            }}
        />
    </div>
);

type TableExpandButtonProps = Omit<ButtonProps<'button'>, 'color' | 'iconOnly' | 'size' | 'variant'> & {
    readonly $isExpanded?: boolean;
};

export const TableExpandButton: FunctionComponent<TableExpandButtonProps> = ({
    $isExpanded = false,
    className,
    ...props
}) => (
    <Button
        className={clsx(
            '!rounded-xs !justify-center !mr-s !p-xs',
            $isExpanded ? '[&_svg]:rotate-90' : '[&_svg]:rotate-0',
            '[&_svg]:transition-transform [&_svg]:duration-200 [&_svg]:ease-in-out',
            className,
        )}
        iconOnly={true}
        size="xs"
        variant="ghost"
        {...props}
    />
);

export const TableNameCell: FunctionComponent<HTMLAttributes<HTMLDivElement> & { readonly $level?: number }> = ({
    $level = 0,
    className,
    style,
    ...props
}) => (
    <div
        className={clsx('flex flex-row items-center', className)}
        style={{ paddingLeft: `${$level * 3}rem`, ...style }}
        {...props}
    />
);

export const TableNameCellContent: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={clsx('flex flex-col items-start gap-s', className)} {...props} />
);

type TableActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    readonly children: ReactNode;
    readonly variant?: 'ghost';
};

export const TableActionButton: FunctionComponent<TableActionButtonProps> = ({
    className,
    color: _color,
    variant: _variant,
    ...props
}) => (
    <Button
        className={clsx(
            '!inline-flex !items-center !justify-center !p-xs',
            '!bg-transparent !border-none !text-gray-700 !text-sm !cursor-pointer',
            'hover:!bg-gray-100 hover:!text-gray-700',
            className,
        )}
        size="xs"
        variant="ghost"
        {...props}
    />
);

export const TableCell: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div
        className={clsx(
            'flex items-center text-gray-800 text-xxs min-h-[48px] py-m px-l transition-all duration-200 ease-in-out',
            className,
        )}
        {...props}
    />
);

export const TableLoadingSpinner: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={clsx('flex items-center justify-center text-gray-600 text-base p-xl', className)} {...props} />
);

export const TableErrorMessage: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div
        className={clsx(
            'flex items-center justify-center',
            'bg-error-50 border border-error-600 rounded-md text-error-600 text-base m-l p-xl',
            className,
        )}
        {...props}
    />
);

type SkeletonRowProps = {
    readonly columnCount: number;
    readonly columnWidths: ReadonlyArray<TableColumnWidth>;
};

export const TableSkeletonRow: FunctionComponent<SkeletonRowProps> = ({ columnCount, columnWidths }) => (
    <TableRow $columnWidths={columnWidths} data-testid="skeleton" role="row">
        {Array.from({ length: columnCount }).map((_, cellIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
            <TableCell key={cellIndex} role="gridcell">
                <SkeletonBase className="h-xl w-[80%]" />
            </TableCell>
        ))}
    </TableRow>
);

type SkeletonHeaderRowProps = {
    readonly columnCount: number;
    readonly columnWidths: ReadonlyArray<TableColumnWidth>;
};

export const TableSkeletonHeaderRow: FunctionComponent<SkeletonHeaderRowProps> = ({ columnCount, columnWidths }) => (
    <TableHeader $columnWidths={columnWidths} data-testid="skeleton" role="row">
        {Array.from({ length: columnCount }).map((_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
            <TableHeaderCell key={index} role="columnheader">
                <SkeletonBase className="h-l w-[70%]" />
            </TableHeaderCell>
        ))}
    </TableHeader>
);

type TableSkeletonProps = {
    readonly 'aria-label'?: string;
    readonly columnWidths: ReadonlyArray<TableColumnWidth>;
    readonly 'data-testid'?: string;
    readonly rowCount?: number;
};

export const TableSkeleton: FunctionComponent<TableSkeletonProps> = ({
    'aria-label': ariaLabel = 'Loading table',
    columnWidths,
    'data-testid': dataTestId = 'table-skeleton',
    rowCount = 5,
}) => {
    const columnCount = columnWidths.length;

    return (
        <TableContainer>
            <TableGrid aria-label={ariaLabel} data-testid={dataTestId} role="grid">
                <TableSkeletonHeaderRow columnCount={columnCount} columnWidths={columnWidths} />
                <TableBody>
                    {Array.from({ length: rowCount }).map((_, rowIndex) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                        <TableSkeletonRow key={rowIndex} columnCount={columnCount} columnWidths={columnWidths} />
                    ))}
                </TableBody>
            </TableGrid>
        </TableContainer>
    );
};

type TableProps = {
    readonly 'aria-label'?: string;
    readonly children: ReactNode;
    readonly 'data-testid'?: string;
    readonly style?: CSSProperties;
};

export const Table: FunctionComponent<TableProps> = ({
    'aria-label': ariaLabel,
    children,
    'data-testid': dataTestId,
    ...props
}) => (
    <TableContainer {...props}>
        <TableGrid aria-label={ariaLabel} data-testid={dataTestId} role="grid">
            {children}
        </TableGrid>
    </TableContainer>
);
